import _ from 'lodash';
import { createTypes } from 'reduxsauce';
import StorageService from './StorageService';
import TimetableApi from './TimetableApi';
import { titleCase } from '../utils/Humanizers';
import AliasMacronService from './AliasMacronService';

const timetableAPI = TimetableApi.getInstance();

const MAX_RECENT_ROUTE_SEARCHES_STORAGE_SIZE = 10;
const MAX_RECENT_ROUTE_SEARCHES_DISPLAY = 3;

export const SEARCH_MODE = createTypes(`
    BUS
    TRAIN
    FERRY
`);

export const searchModeRouteTypeMap = {
  [SEARCH_MODE.BUS]: 3,
  [SEARCH_MODE.TRAIN]: 2,
  [SEARCH_MODE.FERRY]: 4,
};

export const noRoutesFoundMsg = 'There are no routes found for';

// TODO: Need to have a more consistent naming for all these methods
export const renderNoRoutesMessage = query => `${noRoutesFoundMsg} "${query}"`;

export const selectionIsNotFoundMessage = selection => _.isString(selection) && selection.indexOf(noRoutesFoundMsg) >= 0;
export const getRouteLabel = route => (selectionIsNotFoundMessage(route) ? route : `${route.route_short_name} - to ${titleCase(route.last_stop_headsign)}`);
export const getRouteLabelDisplay = route => AliasMacronService.getMacronisedText((selectionIsNotFoundMessage(route) ? route : `${route.route_short_name} - to ${titleCase(route.last_stop_headsign)}`));
export const getStopDescription = stop => `Stop ${stop.stop_code} - ${stop.stop_name}`;
export const getStopDescriptionDisplay = stop => AliasMacronService.getMacronisedText(`Stop ${stop.stop_code} - ${stop.stop_name}`);

const getMappedRouteLabel = (route) => {
  let mappedShortName = route.route_short_name;
  if (!_.isEmpty(route.display_name)) {
    mappedShortName = `${route.route_short_name} ${route.display_name}`;
  }

  return selectionIsNotFoundMessage(route) ? route : `${mappedShortName} - to ${titleCase(route.last_stop_headsign)}`;
};

const normalizeDiacritics = (s) => {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .normalize("NFC");
}

export function getTimetableRoutes({ text, searchMode }) {
  const routeTypes = searchModeRouteTypeMap[searchMode];
  const matchingAlias = AliasMacronService.getAlias(text);

  if (matchingAlias) {
    const p1 = timetableAPI.getTimetableRoutes({ text: normalizeDiacritics(text), routeTypes });
    const p2 = timetableAPI.getTimetableRoutes({ text: matchingAlias, routeTypes });
    return Promise.all([p1, p2]).then(([a, b]) => a.concat(b));
  }

  return timetableAPI.getTimetableRoutes({ text: normalizeDiacritics(text), routeTypes });
}

export function getStopsByShapeId({ shapeIds }) {
  return timetableAPI.getStopsByShapeId({ shapeIds });
}

export function getTimetableSchedule(...args) {
  return timetableAPI.getTimetableSchedule(...args);
}


function filterRecentRouteSearches(query) {
  const lowerCaseQuery = query.toLowerCase();
  // the query must match from start of a word only, e.g. when querying 'market', 'newmarket' should NOT match
  const matchFunc = suggestion => suggestion && new RegExp(`\\b${_.escapeRegExp(lowerCaseQuery)}`, 'gi').test(getMappedRouteLabel(suggestion));

  return (suggestions) => {
    if (suggestions && suggestions.length > 0) {
      return _.filter(suggestions, matchFunc).slice(0, MAX_RECENT_ROUTE_SEARCHES_DISPLAY);
    }
    return [];
  };
}

export function getRecentRouteSearches(storageKey, query) {
  return StorageService.getItem(storageKey)
    .then(filterRecentRouteSearches(query));
}

export function saveRouteSearch(suggestion, storageKey) {
  return StorageService
    .getItem(storageKey)
    .then((prevList) => {
      const newList = prevList || [];
      const index = _.findIndex(newList, suggestion);
      // if already included remove (and reinsert to the top)
      if (index !== -1) {
        newList.splice(index, 1);
      }
      // add the address to the recent list when it is not included currently.
      const length = newList.unshift(suggestion);
      if (length > MAX_RECENT_ROUTE_SEARCHES_STORAGE_SIZE) {
        newList.pop();
      }
      return newList;
    })
    .then(list => StorageService.setItem(storageKey, list));
}

// This is a temporary function with a one time use to clear previous
// recent searches cache that uses outdate structure base on the previous GTFS data
// Will be a dead code overtime
export function removeOutdateRecentSearches() {
  return Promise.all(
    Object.keys(SEARCH_MODE).map(keys => StorageService.removeItem(`timetables-recent-${keys}`)),
  );
}